import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// 'YYYY-MM-DD' → local Date at noon (never UTC midnight, so DST/timezones can't shift the day)
export function parseYMD(value) {
  if (value instanceof Date) { const d = new Date(value); d.setHours(12, 0, 0, 0); return d; }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  const d = new Date(value); d.setHours(12, 0, 0, 0); return d;
}

// Local Date → 'YYYY-MM-DD' (no UTC conversion)
export function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Add whole days to a 'YYYY-MM-DD' string, DST-safe
export function addDaysYMD(ymd, days) {
  const d = parseYMD(ymd);
  d.setDate(d.getDate() + days);
  return toYMD(d);
}

export function getWeekStart(date = new Date()) {
  const d = parseYMD(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); // Adjust to Monday
  return toYMD(d);
}

export function getWeekEnd(weekStart) {
  return addDaysYMD(weekStart, 6);
}

export function getWeekRange(weekStart) {
  const start = parseYMD(weekStart);
  const end = parseYMD(weekStart);
  end.setDate(end.getDate() + 6);
  
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}


/**
 * Sort a list of units so ACTIVE units come first (sorted by unit_number),
 * followed by INACTIVE units (also sorted by unit_number). This is the standard
 * ordering used in every unit dropdown across the app.
 */
export function sortUnitsForSelect(units) {
  if (!Array.isArray(units)) return [];
  const active = [];
  const inactive = [];
  for (const u of units) {
    if ((u?.status || 'active') === 'active') active.push(u);
    else inactive.push(u);
  }
  const byNum = (a, b) => String(a.unit_number || '').localeCompare(String(b.unit_number || ''), undefined, { numeric: true });
  active.sort(byNum);
  inactive.sort(byNum);
  return [...active, ...inactive];
}

/**
 * True if the unit is marked inactive (or has any status other than 'active').
 */
export function isUnitInactive(unit) {
  return unit && (unit.status || 'active') !== 'active';
}


/**
 * Normalize a FastAPI/axios error into a plain string suitable for toast.error().
 *
 * FastAPI 422 responses put a list of `{type, loc, msg, input, url}` objects in
 * `detail`; passing that array to a toast/JSX child throws
 * "Objects are not valid as a React child" and crashes the page.  Any string
 * (403/404/409/500 detail) is returned as-is.
 */
export function apiErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => {
        if (!d) return '';
        if (typeof d === 'string') return d;
        const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${d.msg || 'invalid'}` : (d.msg || 'invalid');
      })
      .filter(Boolean)
      .join('; ') || fallback;
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || fallback;
  }
  // ── Friendly network / edge errors (Cloudflare 5xx, timeouts, offline) ──
  // Never surface raw HTML from Cloudflare / nginx / other CDNs; users see
  // scary "origin web server could not parse" strings otherwise.
  const status = error?.response?.status;
  const raw = error?.response?.data;
  const rawText = typeof raw === 'string' ? raw : '';
  if (rawText.toLowerCase().includes('cloudflare') || /<!doctype html/i.test(rawText)) {
    return 'The server took too long to respond. Please try again in a moment.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The server is briefly unavailable. Please try again in a moment.';
  }
  if (status === 520 || status === 521 || status === 522 || status === 524) {
    return 'The upload timed out. Try uploading a smaller batch of files, or try again in a moment.';
  }
  if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
    return 'The request took too long. Please try again — if it keeps timing out, try a smaller file.';
  }
  if (error?.message === 'Network Error') {
    return 'You appear to be offline. Please check your connection and try again.';
  }
  return error?.message || fallback;
}
